import { Link } from 'react-router-dom';
import { Star, Phone, MapPin, Clock, BadgeCheck, ChevronRight } from 'lucide-react';
import type { ServiceProvider } from '@/types';
import { SERVICE_TYPE_LABELS } from '@/types';

interface ProviderPopupProps {
  provider: ServiceProvider;
  distance?: number;
}

export function ProviderPopup({ provider, distance }: ProviderPopupProps) {
  // Check if currently open (simplified - would need proper logic)
  const isOpen = true; // Placeholder

  return (
    <div className="w-72 overflow-hidden">
      {/* Cover Image with gradient overlay */}
      <div className="relative h-28">
        {provider.coverImageUrl ? (
          <img
            src={provider.coverImageUrl}
            alt={provider.businessName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700" />
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Verified badge */}
        {provider.isVerified && (
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full shadow-sm">
            <BadgeCheck className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-[10px] font-semibold text-gray-700">Verified</span>
          </div>
        )}

        {/* Logo overlay */}
        {provider.logoUrl && (
          <div className="absolute -bottom-5 left-3">
            <img
              src={provider.logoUrl}
              alt=""
              className="w-12 h-12 rounded-xl border-2 border-white shadow-md object-cover bg-white"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className={`p-3 ${provider.logoUrl ? 'pt-7' : 'pt-3'}`}>
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2">
            {provider.businessName}
          </h3>
          {provider.averageRating && (
            <div className="flex items-center gap-0.5 bg-gray-900 text-white px-1.5 py-0.5 rounded-md flex-shrink-0">
              <Star className="w-3 h-3 fill-current" />
              <span className="text-xs font-semibold">{provider.averageRating.toFixed(1)}</span>
            </div>
          )}
        </div>

        {/* Meta info */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
          {provider.reviewCount > 0 && (
            <span>{provider.reviewCount} review{provider.reviewCount !== 1 ? 's' : ''}</span>
          )}
          {distance && (
            <span className="flex items-center gap-0.5">
              <MapPin className="w-3 h-3" />
              {distance < 1 ? `${(distance * 5280).toFixed(0)} ft` : `${distance.toFixed(1)} mi`}
            </span>
          )}
          <span className={`flex items-center gap-0.5 ${isOpen ? 'text-green-600' : 'text-red-500'}`}>
            <Clock className="w-3 h-3" />
            {isOpen ? 'Open' : 'Closed'}
          </span>
        </div>

        {/* Services */}
        <div className="flex flex-wrap gap-1 mb-3">
          {provider.serviceTypes.slice(0, 3).map((type) => (
            <span
              key={type}
              className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[10px] font-medium"
            >
              {SERVICE_TYPE_LABELS[type]}
            </span>
          ))}
          {provider.serviceTypes.length > 3 && (
            <span className="px-2 py-0.5 text-gray-400 text-[10px]">
              +{provider.serviceTypes.length - 3}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {provider.phone && (
            <a
              href={`tel:${provider.phone}`}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-semibold text-gray-700 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Phone className="w-3.5 h-3.5" />
              Call Now
            </a>
          )}
          <Link
            to={`/provider/${provider.slug}`}
            className="flex-1 flex items-center justify-center gap-1 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg text-xs font-semibold text-white transition-colors"
          >
            View Profile
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
